import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { toLocalDateString } from '../../utils/dateUtils';

export interface MiniMonthCalendarProps {
  /** Any date within the month being shown */
  monthDate: Date;
  /** Selected date (YYYY-MM-DD) */
  selectedDate: string;
  /** Called when a day is selected (YYYY-MM-DD) */
  onSelectDate: (dateStr: string) => void;
  /** Whether a day has events */
  hasEvent: (dateStr: string) => boolean;
  /** Navigate months */
  onPrevMonth?: () => void;
  onNextMonth?: () => void;
  /** Optional header content aligned between title and nav */
  headerCenter?: React.ReactNode;
  /** Hide built-in header (use when embedding under an external header) */
  showHeader?: boolean;
  /** Compact sizing for tighter layouts */
  size?: 'sm' | 'md';
  /** Disable selection */
  disabled?: boolean;
}

const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const MiniMonthCalendar: React.FC<MiniMonthCalendarProps> = ({
  monthDate,
  selectedDate,
  onSelectDate,
  hasEvent,
  onPrevMonth,
  onNextMonth,
  headerCenter,
  showHeader = true,
  size = 'md',
  disabled = false,
}) => {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startingDayOfWeek = firstDay.getDay(); // Sunday=0
  const daysInMonth = lastDay.getDate();

  const todayStr = toLocalDateString(new Date());

  const grid: Array<null | { day: number; dateStr: string; isToday: boolean; isSelected: boolean; hasEvent: boolean }> = [];

  for (let i = 0; i < startingDayOfWeek; i++) grid.push(null);

  for (let day = 1; day <= daysInMonth; day++) {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    grid.push({
      day,
      dateStr,
      isToday: dateStr === todayStr,
      isSelected: dateStr === selectedDate,
      hasEvent: hasEvent(dateStr),
    });
  }

  const monthLabel = monthDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const cellHeight = size === 'sm' ? 'h-7' : 'h-8';
  const cellText = size === 'sm' ? 'text-[11px]' : 'text-xs';

  return (
    <div className="card p-0 overflow-hidden">
      {showHeader && (
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border-light dark:border-border-dark">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-text-primary dark:text-text-dark-primary truncate">
              {monthLabel}
            </h3>
          </div>

          {headerCenter ? <div className="min-w-0">{headerCenter}</div> : <div className="flex-1" />}

          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={onPrevMonth}
              disabled={!onPrevMonth || disabled}
              className="p-1.5 rounded hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Previous month"
            >
              <ChevronLeft className="h-4 w-4 text-text-secondary dark:text-text-dark-secondary" />
            </button>
            <button
              type="button"
              onClick={onNextMonth}
              disabled={!onNextMonth || disabled}
              className="p-1.5 rounded hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Next month"
            >
              <ChevronRight className="h-4 w-4 text-text-secondary dark:text-text-dark-secondary" />
            </button>
          </div>
        </div>
      )}

      <div className="px-4 py-3">
        <div className="grid grid-cols-7 gap-1 mb-2">
          {dayNames.map((day) => (
            <div
              key={day}
              className="text-[11px] font-medium text-text-tertiary dark:text-text-dark-tertiary text-center py-1"
            >
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {grid.map((cell, idx) => {
            if (!cell) {
              return <div key={`empty-${idx}`} className={`${cellHeight}`} />;
            }

            const base =
              'rounded cursor-pointer transition-colors relative flex items-center justify-center select-none';

            const selectedClasses = cell.isSelected
              ? 'bg-primary-600 text-white font-semibold'
              : cell.isToday
                ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-semibold'
                : cell.hasEvent
                  ? 'bg-background-tertiary dark:bg-background-dark-tertiary text-text-primary dark:text-text-dark-primary hover:bg-neutral-200 dark:hover:bg-background-dark-secondary'
                  : 'text-text-secondary dark:text-text-dark-secondary hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary';

            return (
              <button
                key={cell.dateStr}
                type="button"
                onClick={() => onSelectDate(cell.dateStr)}
                disabled={disabled}
                className={`${cellHeight} ${cellText} ${base} ${selectedClasses} disabled:opacity-50 disabled:cursor-not-allowed`}
                aria-label={`Select ${cell.dateStr}`}
              >
                {cell.day}
                {cell.hasEvent && !cell.isSelected && !cell.isToday && (
                  <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 bg-primary-600 dark:bg-primary-400 rounded-full" />
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default MiniMonthCalendar;

